import { gql } from "@apollo/client";
import client, { refreshTokens } from "../apolloClient";

export interface DeviceLogDay {
  /** IST calendar day, YYYY-MM-DD */
  date: string;
  fileCount: number;
  totalBytes: number;
  firstAt: string | null;
  lastAt: string | null;
}

export interface DeviceLogSummary {
  deviceId: string;
  lastReceivedAt: string | null;
  retentionDays: number;
  days: DeviceLogDay[];
}

export interface DeviceLogFile {
  id: string;
  startedAt: string;
  endedAt: string;
  sizeBytes: number;
  lineCount: number;
  appVersion: string | null;
  receivedAt: string;
}

const DEVICE_LOG_SUMMARY = gql`
  query DeviceLogSummary($deviceId: ID!) {
    deviceLogSummary(deviceId: $deviceId) {
      deviceId
      lastReceivedAt
      retentionDays
      days {
        date
        fileCount
        totalBytes
        firstAt
        lastAt
      }
    }
  }
`;

const DEVICE_LOG_FILES = gql`
  query DeviceLogFiles($deviceId: ID!, $date: String!) {
    deviceLogFiles(deviceId: $deviceId, date: $date) {
      id
      startedAt
      endedAt
      sizeBytes
      lineCount
      appVersion
      receivedAt
    }
  }
`;

/** Snapshot an Android TV attaches to every heartbeat (all fields optional). */
export interface HeartbeatDiagnostics {
  collectedAt?: string;
  receivedAt?: string;
  appVersion?: string;
  transport?: string | null;
  internetValidated?: boolean | null;
  ssid?: string | null;
  rssi?: number | null;
  linkSpeedMbps?: number | null;
  gateway?: string | null;
  mqttConnected?: boolean;
  mqttReconnects?: number;
  deviceUptimeSeconds?: number | null;
  consecutiveFailures?: number;
  pendingOutages?: number;
  /** Log files waiting on the box to upload (null = unknown / old app). */
  queuedLogChunks?: number | null;
}

export interface OutageReport {
  id: string;
  message: string;
  createdAt: string;
  metadata: {
    kind?: "heartbeat_failed" | "heartbeat_gap";
    startedAt?: string;
    endedAt?: string | null;
    verdict?: string | null;
    gapCause?: string;
    failedHeartbeats?: number;
    probes?: Array<{ side?: string }>;
  } | null;
}

export interface DeviceConnectivity {
  deviceId: string;
  lastSeenAt: string | null;
  lastDiagnostics: HeartbeatDiagnostics | null;
  outages: OutageReport[];
}

const DEVICE_CONNECTIVITY = gql`
  query DeviceConnectivity($deviceId: ID!, $limit: Int) {
    deviceConnectivity(deviceId: $deviceId, limit: $limit) {
      deviceId
      lastSeenAt
      lastDiagnostics
      outages {
        id
        message
        metadata
        createdAt
      }
    }
  }
`;

export interface DevicePingResult {
  ok: boolean;
  /** NO_REPLY | BROKER_UNAVAILABLE | NOT_SUPPORTED | NOT_ASSIGNED */
  error: string | null;
  roundTripMs: number | null;
  diagnostics: HeartbeatDiagnostics | null;
  sentAt: string;
}

const PING_DEVICE = gql`
  mutation PingDevice($deviceId: ID!) {
    pingDevice(deviceId: $deviceId) {
      ok
      error
      roundTripMs
      diagnostics
      sentAt
    }
  }
`;

// The download is a plain-text stream from a REST route next to /graphql —
// a day of logs can run to tens of MB, too big for a GraphQL response.
const restBaseUrl = () =>
  (process.env.REACT_APP_API_URL || "").replace(/\/graphql\/?$/, "");

const fileNameFrom = (res: Response, fallback: string) => {
  const match = /filename="([^"]+)"/.exec(
    res.headers.get("Content-Disposition") || "",
  );
  return match?.[1] || fallback;
};

export const deviceLogFileService = {
  ping: async (deviceId: string): Promise<DevicePingResult> => {
    const { data } = await client.mutate({
      mutation: PING_DEVICE,
      variables: { deviceId },
    });
    return data.pingDevice;
  },

  getConnectivity: async (
    deviceId: string,
    limit = 30,
  ): Promise<DeviceConnectivity | null> => {
    const { data } = await client.query({
      query: DEVICE_CONNECTIVITY,
      variables: { deviceId, limit },
      fetchPolicy: "network-only",
    });
    return data.deviceConnectivity;
  },

  getSummary: async (deviceId: string): Promise<DeviceLogSummary> => {
    const { data } = await client.query({
      query: DEVICE_LOG_SUMMARY,
      variables: { deviceId },
      fetchPolicy: "network-only",
    });
    return data.deviceLogSummary;
  },

  listFiles: async (deviceId: string, date: string): Promise<DeviceLogFile[]> => {
    const { data } = await client.query({
      query: DEVICE_LOG_FILES,
      variables: { deviceId, date },
      fetchPolicy: "network-only",
    });
    return data.deviceLogFiles;
  },

  /** Downloads a day (or one chunk of it) as a .txt file. */
  download: async (
    deviceId: string,
    date: string,
    fileId?: string,
  ): Promise<void> => {
    const params = new URLSearchParams({ deviceId, date });
    if (fileId) params.set("fileId", fileId);
    const url = `${restBaseUrl()}/api/device-logs/download?${params}`;

    const request = () =>
      fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

    let res = await request();
    if (res.status === 401 && (await refreshTokens())) {
      res = await request();
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Download failed (${res.status})`);
    }

    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = fileNameFrom(res, `device-logs_${date}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
  },
};
